/*
  assets/littleg-game-focus-contracts.js — GAME-FOCUS-CONTRACT-02
  Owner: existing /pages/game screen-entry and history-return focus transfer.

  Runtime dependency:
  - assets/littleg-daily-mission-flow.js emits littleg:screen-change.

  Boundary:
  - No screen routing, history mutation, visual styling, storage,
    navigation or product-data ownership.
*/
(function littleGGameFocusContracts(window, document) {
  'use strict';

  var VERSION = '2026-08-06-game-focus-contract-02';
  var GLOBAL_KEY = '__LITTLEG_GAME_FOCUS_CONTRACTS_RUNTIME__';
  var ROOT_SELECTOR = '[data-littleg-game-app]';
  var TRIGGER_SELECTOR =
    '[data-lg-screen-link],[data-lg-back],[data-lg-skip],[data-lg-component="place-hotspot"]';
  /** @type {Set<DirectoryRoot>} */
  const mountedRoots = new Set();
  var stateByRoot = new WeakMap();

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].boot === 'function') {
    window[GLOBAL_KEY].boot(document);
    return;
  }

  function clean(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase();
  }

  function list(root, selector) {
    return root && root.querySelectorAll
      ? Array.prototype.slice.call(root.querySelectorAll(selector))
      : [];
  }

  function rootFor(node) {
    return node && node.closest ? node.closest(ROOT_SELECTOR) : null;
  }

  function stateFor(root) {
    var state = stateByRoot.get(root);
    if (state) return state;

    state = {
      currentView: clean(root.dataset.currentView),
      pending: null,
      pendingBack: false,
      stack: [],
      frame: null,
      active: true,
    };
    stateByRoot.set(root, state);
    mountedRoots.add(root);
    root.setAttribute('data-lg-focus-contract', VERSION);
    return state;
  }

  function targetView(control) {
    if (!control) return '';
    if (control.hasAttribute('data-lg-back')) return '__back__';
    if (control.hasAttribute('data-lg-skip')) return 'guess';
    return clean(
      control.getAttribute('data-lg-screen-link') ||
        control.getAttribute('data-lg-target-view') ||
        ''
    );
  }

  function rememberTrigger(event) {
    var raw = event.target;
    var control = raw && raw.closest ? raw.closest(TRIGGER_SELECTOR) : null;
    var root = rootFor(control);
    if (!root || !control || control.disabled || control.getAttribute('aria-disabled') === 'true')
      return;

    var state = stateFor(root);
    var toView = targetView(control);
    var fromView = clean(root.dataset.currentView || state.currentView);

    if (toView === '__back__') {
      state.pendingBack = true;
      return;
    }

    if (fromView && toView && fromView !== toView) {
      state.pending = {
        fromView: fromView,
        toView: toView,
        trigger: control,
      };
    }
  }

  function focusNode(node) {
    if (
      !node ||
      node.isConnected === false ||
      node.closest('[hidden],[aria-hidden="true"],[inert]')
    )
      return false;

    var hadTabindex = node.hasAttribute('tabindex');
    if (!hadTabindex) node.setAttribute('tabindex', '-1');

    try {
      node.focus({ preventScroll: true });
    } catch (_error) {
      try {
        node.focus();
      } catch (_focusError) {
        if (!hadTabindex) node.removeAttribute('tabindex');
        return false;
      }
    }

    if (!hadTabindex) {
      node.addEventListener(
        'blur',
        function restoreTabindex() {
          node.removeAttribute('tabindex');
        },
        { once: true }
      );
    }
    return document.activeElement === node;
  }

  function focusScreenEntry(screen) {
    if (!screen) return false;
    var target = screen.querySelector(
      '[data-lg-screen-focus],h1,h2,[role="heading"],.lg-game-screen__body'
    );
    return target ? focusNode(target) : false;
  }

  function restoreFromStack(state, view) {
    for (var index = state.stack.length - 1; index >= 0; index -= 1) {
      var entry = state.stack[index];
      if (entry.fromView !== view) continue;
      state.stack.splice(index, 1);
      if (focusNode(entry.trigger)) return true;
    }
    return false;
  }

  function screenVisible(screen) {
    if (!screen || screen.hidden || screen.getAttribute('aria-hidden') === 'true') return false;
    if (screen.closest('[hidden],[aria-hidden="true"],[inert]')) return false;
    return true;
  }

  function rootsForChange(event, view) {
    var eventRoot = rootFor(event && event.target);
    if (eventRoot) return [eventRoot];

    var matches = [];
    mountedRoots.forEach(function (root) {
      var screen = root.querySelector('[data-lg-screen="' + view + '"]');
      if (screenVisible(screen)) matches.push(root);
    });

    if (matches.length) return matches;
    if (mountedRoots.size === 1) return [mountedRoots.values().next().value];
    return [];
  }

  function transfer(root, view, source) {
    var state = stateFor(root);
    var screen = root.querySelector('[data-lg-screen="' + view + '"]');

    if (source === 'initial') {
      state.currentView = view;
      state.pending = null;
      state.pendingBack = false;
      return;
    }

    if (state.pending && state.pending.toView === view) {
      state.stack.push(state.pending);
      if (state.stack.length > 20) state.stack.splice(0, state.stack.length - 20);
      state.pending = null;
    }

    if (state.frame != null) window.cancelAnimationFrame(state.frame);
    state.frame = window.requestAnimationFrame(function transferFocus() {
      state.frame = null;
      if (!state.active || root.isConnected === false) return;

      var isReturn = source === 'popstate' || source === 'back-fallback' || state.pendingBack;
      if (!isReturn || !restoreFromStack(state, view)) focusScreenEntry(screen);

      state.pendingBack = false;
      state.currentView = view;
      root.setAttribute('data-lg-focus-view', view);
    });
  }

  function handleScreenChange(event) {
    var detail = event && event.detail ? event.detail : {};
    var view = clean(detail.view);
    var source = clean(detail.source);
    if (!view) return;

    rootsForChange(event, view).forEach(function (root) {
      transfer(root, view, source);
    });
  }

  function mount(root) {
    if (!root || stateByRoot.has(root)) return;
    stateFor(root);
  }

  function destroy(root) {
    var state = root ? stateByRoot.get(root) : null;
    if (!state) return;

    state.active = false;
    if (state.frame != null) window.cancelAnimationFrame(state.frame);
    state.pending = null;
    state.stack.length = 0;

    mountedRoots.delete(root);
    stateByRoot.delete(root);
    root.removeAttribute('data-lg-focus-contract');
    root.removeAttribute('data-lg-focus-view');
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) mount(target);
    list(target, ROOT_SELECTOR).forEach(mount);
  }

  function destroyAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) destroy(target);
    list(target, ROOT_SELECTOR).forEach(destroy);
  }

  document.addEventListener('pointerdown', rememberTrigger, true);
  document.addEventListener(
    'keydown',
    function rememberKeyboardTrigger(event) {
      if (event.key === 'Enter' || event.key === ' ') rememberTrigger(event);
    },
    true
  );
  document.addEventListener('littleg:screen-change', handleScreenChange);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroyAll(event.target || document);
  });

  window[GLOBAL_KEY] = {
    version: VERSION,
    boot: boot,
    destroy: destroyAll,
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot(document);
      },
      { once: true }
    );
  } else {
    boot(document);
  }
})(window, document);
